import { useCopy } from "../../shared/i18n";
import { MARKETING_COPY, SECTION_IDS } from "./copy";
import { useSectionSpy } from "./useSectionSpy";
import { SiteHeader } from "./sections/SiteHeader";
import { Hero } from "./sections/Hero";
import { TrustStrip } from "./sections/TrustStrip";
import { HowItWorks } from "./sections/HowItWorks";
import { GamesShowcase } from "./sections/GamesShowcase";
import { Privacy } from "./sections/Privacy";
import { Audiences } from "./sections/Audiences";
import { Faq } from "./sections/Faq";
import { FinalCta } from "./sections/FinalCta";
import { SiteFooter } from "./sections/SiteFooter";

/**
 * The website's home page — everything a school needs to decide whether to
 * book a demo, and a free game for anyone who wants proof first.
 *
 * 🔴 THE ORDER IS THE ORDER OF A BUYER'S QUESTIONS. What is it (hero) → can I
 * trust it (trust strip) → how would my class use it (how it works) → what will
 * the children actually play (games) → what happens to the camera (privacy) →
 * what is in it for my teachers and parents (who it's for) → the questions left
 * over (FAQ) → book it (closing banner). Each section answers one question and
 * hands the reader to the next.
 *
 * 🔴 THIS FILE COMPOSES; IT DOES NOT DECORATE. Copy lives in `copy.ts` (typed,
 * so a half-translated section does not compile), the games come from the
 * `GAMES` registry via `games.ts`, the art from `assets.ts`, and each section
 * owns its own file with the reasoning for its design beside it.
 *
 * ⚠️ THE HEADER FLOATS OVER THE HERO (`overlay`), so the hero starts at the top
 * of the page and supplies its own top padding. Every other page with this
 * header and no hero (Connect) leaves `overlay` off and gets the sticky bar.
 */
export interface WelcomeScreenProps {
  /** Opens the door picker (`#/start`). */
  onSignIn?: () => void;
}

export function WelcomeScreen({ onSignIn }: WelcomeScreenProps) {
  const t = useCopy(MARKETING_COPY);
  const spy = useSectionSpy();

  return (
    <div className="dash-text min-h-screen overflow-x-clip bg-surface font-dash text-ink selection:bg-primary-tint selection:text-primary">
      {/*
        The first thing a keyboard or screen-reader user meets — hidden until
        focused, which is the only correct way to build a skip link.
      */}
      <a
        href={`#${SECTION_IDS.how}`}
        onClick={(e) => {
          e.preventDefault();
          spy.goTo("how");
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-100 focus:rounded-pill focus:bg-primary focus:px-5 focus:py-3 focus:font-dash focus:text-dash-sm focus:font-bold focus:text-primary-ink"
      >
        {t.skipToContent}
      </a>

      <SiteHeader t={t} spy={spy} onSignIn={onSignIn} overlay />

      <main>
        <Hero t={t} />
        <TrustStrip t={t} />
        <HowItWorks t={t} />
        <GamesShowcase t={t} />
        <Privacy t={t} />
        <Audiences t={t} />
        <Faq t={t} />
        <FinalCta t={t} />
      </main>

      <div className="mt-24 lg:mt-32">
        <SiteFooter t={t} />
      </div>
    </div>
  );
}
